import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Segmented, cn, type IconName } from "@sagot/ui";
import { useFormatters, type BillingOverview, type BillingPlan } from "../shared";
import { addedFeatures, annualMonthsFree, planDirection, planPrice, storageText, type BillingInterval } from "./billing-model";
import { GatedButton } from "./gated-button";

/** Features the Institution card lists on their own line (the plan is quoted, so it has no "+" list). */
const CUSTOM_FEATURE_ICONS: Readonly<Record<string, IconName>> = { private_deployment: "server", sla: "shield-check", customer_success_manager: "headset", secure_launch: "log-in" };

interface PlansSectionProps {
  plans: readonly BillingPlan[];
  overview: BillingOverview;
  interval: BillingInterval;
  onIntervalChange: (interval: BillingInterval) => void;
  canManage: boolean;
  onSelect: (plan: BillingPlan) => void;
  /** Opens the sales request form for this plan (Institution, or "Pay manually / talk to sales"). */
  onContactSales: (plan: BillingPlan) => void;
}

function Bullet({ icon = "check", tone = "text-success", children }: { icon?: IconName; tone?: string; children: string }) {
  return (
    <li className="flex gap-2">
      <Icon name={icon} className={cn("mt-0.5 size-4 shrink-0", tone)} />
      {children}
    </li>
  );
}

function PlanBullets({ plan, plans, index }: { plan: BillingPlan; plans: readonly BillingPlan[]; index: number }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const list = new Intl.ListFormat(fmt.locale, { style: "short", type: "conjunction" });
  const feature = (key: string) => t(`billing.features.${key}`, { defaultValue: key });
  if (plan.is_custom) {
    const extras = plan.features.filter((key) => key in CUSTOM_FEATURE_ICONS);
    return (
      <ul className="mt-4 space-y-2 text-sm">
        <Bullet>{t("billing.plans.customVolume")}</Bullet>
        {extras.map((key) => (
          <Bullet key={key} icon={CUSTOM_FEATURE_ICONS[key]} tone="text-primary-ink">
            {feature(key)}
          </Bullet>
        ))}
      </ul>
    );
  }
  const added = addedFeatures(plans, index).filter((key) => !(key in CUSTOM_FEATURE_ICONS));
  return (
    <ul className="mt-4 space-y-2 text-sm">
      {plan.message_credits !== null ? <Bullet>{t("billing.plans.messages", { value: fmt.number(plan.message_credits) })}</Bullet> : null}
      {plan.agents !== null ? <Bullet>{t("billing.plans.agents", { count: plan.agents })}</Bullet> : null}
      {plan.storage_mb !== null ? <Bullet>{t("billing.plans.storage", { value: storageText(plan.storage_mb) })}</Bullet> : null}
      {plan.seats !== null ? <Bullet>{t("billing.plans.seats", { count: plan.seats })}</Bullet> : null}
      {added.length ? (
        <Bullet icon={index === 0 ? "check" : "plus"} tone={index === 0 ? "text-success" : "text-primary-ink"}>
          {list.format(added.map(feature))}
        </Bullet>
      ) : null}
    </ul>
  );
}

interface PlanCtaProps {
  plan: BillingPlan;
  overview: BillingOverview;
  plans: readonly BillingPlan[];
  interval: BillingInterval;
  canManage: boolean;
  onSelect: (plan: BillingPlan) => void;
  onContactSales: (plan: BillingPlan) => void;
}

function PlanCta({ plan, overview, plans, interval, canManage, onSelect, onContactSales }: PlanCtaProps) {
  const { t } = useTranslation();
  const direction = planDirection(plans, overview.plan.key, plan.key);
  if (direction === "custom") {
    return (
      <Button variant="dark" className="w-full" onClick={() => { onContactSales(plan); }}>
        {t("billing.plans.contactSales")}
        <span className="sr-only"> {plan.name}</span>
      </Button>
    );
  }
  const sameInterval = interval === (overview.interval === "annual" ? "annual" : "monthly");
  if (direction === "current" && !sameInterval) {
    return (
      <GatedButton allowed={canManage} variant="primary" className="w-full" onClick={() => { onSelect(plan); }}>
        {t(interval === "annual" ? "billing.plans.switchYearly" : "billing.plans.switchMonthly")}
      </GatedButton>
    );
  }
  if (direction === "current") {
    return (
      <Button variant="secondary" className="w-full" disabled>
        {t("billing.plans.yourPlan")}
      </Button>
    );
  }
  const upgrade = direction === "upgrade";
  return (
    <GatedButton allowed={canManage} variant={upgrade ? "primary" : "secondary"} className="w-full" onClick={() => { onSelect(plan); }}>
      {upgrade ? t("billing.plans.upgradeTo", { plan: plan.name }) : t("billing.plans.downgrade")}
      {upgrade ? null : <span className="sr-only"> {plan.name}</span>}
    </GatedButton>
  );
}

/** `#plans`: plan cards with monthly / yearly prices (yearly = annual price, "2 months free"). */
export function PlansSection({ plans, overview, interval, onIntervalChange, canManage, onSelect, onContactSales }: PlansSectionProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const freeMonths = Math.max(...plans.map(annualMonthsFree), 0);
  return (
    <section id="plans" className="scroll-mt-20" aria-labelledby="plans-title">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="plans-title" className="text-lg font-extrabold tracking-tight">
            {t("billing.plans.title")}
          </h2>
          <p className="text-sm text-fg-muted">
            {t(overview.region.tax.mode === "exclusive" ? "billing.plans.subtitleExclusive" : "billing.plans.subtitleOther", { currency: overview.currency, tax: overview.region.tax.label })}
          </p>
        </div>
        <Segmented
          aria-label={t("billing.plans.period")}
          value={interval}
          onValueChange={onIntervalChange}
          options={[
            { value: "monthly", label: t("billing.plans.monthly") },
            { value: "annual", label: freeMonths ? t("billing.plans.yearlyFree", { count: freeMonths }) : t("billing.plans.yearly") },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan, index) => {
          const current = plan.key === overview.plan.key;
          const price = planPrice(plan, interval);
          return (
            <article key={plan.key} className={cn("card flex flex-col", current && "border-primary ring-1 ring-primary", plan.is_custom && "bg-surface-2")} aria-label={plan.name}>
              <div className="card-body flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold">{plan.name}</h3>
                  {current ? <Badge tone="brand">{t("billing.plans.current")}</Badge> : plan.is_popular ? <Badge tone="warning">{t("billing.plans.recommended")}</Badge> : null}
                </div>
                <div className="mt-2">
                  {price === null ? (
                    <span className="text-2xl font-extrabold">{t("billing.plans.custom")}</span>
                  ) : (
                    <>
                      <span className="text-2xl font-extrabold tabular-nums">{fmt.money(price, plan.currency)}</span>
                      <span className="ml-1 text-sm text-fg-muted">{t(interval === "annual" ? "billing.perYear" : "billing.perMonth")}</span>
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-fg-subtle">{plan.tagline ?? t(`billing.plans.tagline_${plan.key}`, { defaultValue: "" })}</p>
                <PlanBullets plan={plan} plans={plans} index={index} />
              </div>
              <div className="border-t border-line p-4">
                <PlanCta plan={plan} overview={overview} plans={plans} interval={interval} canManage={canManage} onSelect={onSelect} onContactSales={onContactSales} />
                {plan.is_custom ? null : (
                  <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => { onContactSales(plan); }} data-testid={`pay-manually-${plan.key}`}>
                    {t("billing.plans.payManually")}
                    <span className="sr-only"> ({plan.name})</span>
                  </Button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
